<template>
  <div>
    <div class="dealer-accessories-container">
      <div class="dealer-accessories">
        <div class="dealer-accessories__accessory-actions">
          <form-input
            id="dealer-accessories__actions-input"
            v-model.lazy="dealerAccessories"
            class="dealer-accessories__accessory-actions-input"
            title=""
          />
          <div class="dealer-accessories__accessory-actions-buttons">
            <a @click="toggleAllSections(true)">
              {{ i18n.t(`/ProductBlocks/OpenAll`) }}
            </a>
            &nbsp;/&nbsp;
            <a @click="toggleAllSections(false)">
              {{ i18n.t(`/ProductBlocks/CloseAll`) }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <product-categories
      ref="selectedAccessories"
      :title="i18n.t(`/ProductBlocks/DealerAccessories/SelectedAccessories`)"
      :products="selectedProducts"
      :selected-products="selectedProducts"
      :show-purchase-price="showPurchasePrice"
      :expandable="true"
      :expand-on-load="true"
      :show-incl-price="showInclPrice"
      :show-excl-price="showExclPrice"
      :column-name="columnName"
      @add-to-selected="addToSelected"
      @remove-from-selected="removeFromSelected"
      @product-name-click="openPrima"
    />
    <product-categories
      ref="popularAccessories"
      :title="i18n.t(`/ProductBlocks/DealerAccessories/MostSelectedAccessories`)"
      :products="popularProducts"
      :selected-products="selectedProducts"
      :show-purchase-price="showPurchasePrice"
      :expandable="true"
      :expand-on-load="true"
      :show-incl-price="showInclPrice"
      :show-excl-price="showExclPrice"
      :column-name="columnName"
      @add-to-selected="addToSelected"
      @remove-from-selected="removeFromSelected"
      @product-name-click="openPrima"
    />
    <product-categories
      v-for="category in filteredCategoryKeys"
      ref="accessories"
      :key="category"
      :title="category"
      :products="filteredProductCategories[category]"
      :selected-products="selectedProducts"
      :show-purchase-price="showPurchasePrice"
      :expandable="true"
      :expand-on-load="dealerAccessories.length > 0"
      :show-incl-price="showInclPrice"
      :show-excl-price="showExclPrice"
      :column-name="columnName"
      @add-to-selected="addToSelected"
      @remove-from-selected="removeFromSelected"
      @product-name-click="openPrima"
    />
    <div>
      <app-button data-qa="go-to-prima-button" @click="openPrima(null)">
        {{ i18n.t(`/ProductBlocks/DealerAccessories/OpenPrima`) }}
      </app-button>
    </div>
  </div>
</template>

<script>
import ProductCategories from '@/components/generic/presentational/molecules/productCategories/ProductCategories.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import arrayFunctions from '@/helpers/arrayFunctions'
import PrimaUrlHelper from '@/helpers/primaUrlHelper'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    ProductCategories,
    AppButton,
    FormInput,
  },
  provide() {
    return { toggleAll: this.openAll, showProductDescription: true }
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      required: true,
    },
    showPurchasePrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add-to-selected', 'remove-from-selected'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      openAll: {
        value: null,
      },
      categoryNoneName: 'Overige', // Category name to use if categoryDescription == null
      dealerAccessories: '',
    }
  },
  computed: {
    ...mapGetters({
      configuration: ['configuration/configuration'],
      showInclPrice: 'quote/isPriceShowIncl',
      showExclPrice: 'quote/isPriceShowExcl',
    }),
    popularProducts() {
      return this.products
        .filter((x) => {
          // Retreive products in given category and has a selection count
          return this.categories.includes(x.Category) && x.SelectionCount > 0
        })
        .sort((a, b) => {
          // Sort by most selected descending
          return b.SelectionCount - a.SelectionCount
        })
        .slice(0, 6) // Show top 6
    },
    productCategories() {
      return arrayFunctions.groupBy(
        arrayFunctions.sortAlphabetically([...this.products], 'Name'),
        'CategoryDescription',
        this.categoryNoneName
      )
    },
    columnName() {
      return this.showInclPrice ? this.i18n.t(`/common/inclbtw`) : this.i18n.t(`/common/exclbtw`)
    },
    categoryKeys() {
      const categoryKeys = arrayFunctions.sortAlphabetically([
        ...Object.keys(this.productCategories),
      ])
      const categoryNone = categoryKeys.find(
        (x) => x.toLowerCase() === this.categoryNoneName.toLowerCase()
      )
      if (categoryNone) {
        categoryKeys.push(categoryKeys.splice(categoryKeys.indexOf(categoryNone), 1)[0])
      }
      return categoryKeys
    },

    filteredProductCategories() {
      const normalizeString = (str) => {
        if (str === undefined) {
          return ''
        }
        return str.toLowerCase().replace(/[^a-z0-9]/gi, '')
      }

      // Convert dealer accessories search value to lowercase and remove non-alphanumeric characters to make better filtering
      const dealerAccessoriesSearchValue = normalizeString(this.dealerAccessories)

      if (dealerAccessoriesSearchValue === '') {
        return this.productCategories
      }

      const filtered = {}

      for (const category in this.productCategories) {
        // Filter the products in the current category by name to find matches
        const products = this.productCategories[category].filter((product) => {
          const name = normalizeString(product.Name)
          return name.includes(dealerAccessoriesSearchValue)
        })
        if (products.length > 0) {
          filtered[category] = products
        }
      }
      return filtered
    },
    filteredCategoryKeys() {
      const filteredKeys = Object.keys(this.filteredProductCategories)
      // Filter the categoryKeys array to only include keys that exist in the filtered object
      return this.categoryKeys.filter((category) => filteredKeys.includes(category))
    },
  },
  watch: {
    dealerAccessories(newValue) {
      if (newValue.length > 0) {
        if (this.openAll.value) {
          return
        }
        this.toggleAllSections(true)
      } else {
        this.toggleAllSections(false)
      }
    },
  },
  methods: {
    addToSelected(selectedProduct) {
      this.$emit('add-to-selected', selectedProduct)
    },
    removeFromSelected(deselectedProduct) {
      this.$emit('remove-from-selected', deselectedProduct)
    },
    toggleAllSections(expand) {
      this.openAll.value = expand
    },
    openPrima(search) {
      const primaUrl = this.i18n.t(`/ProductBlocks/DealerAccessories/PrimaUrl`)
      if (primaUrl) {
        const primaUrlHelper = new PrimaUrlHelper(primaUrl)

        window.open(
          primaUrlHelper.buildPrimaParams({
            modelYear: this.configuration.CgiModelYear
              ? this.configuration.CgiModelYear
              : this.configuration.ModelYear,
            modelName: this.configuration.CgiModelGroupDescription,
            modelType: this.configuration.CgiModelGenerationDescription,
            trimline: this.configuration.CgiTrimLineDescription
              ? this.configuration.CgiTrimLineDescription
              : this.configuration.TrimlineName,
            search: search,
          })
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dealer-accessories-container {
  & .dealer-accessories {
    &__accessory-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &-input {
        flex: 0 0 70%;
      }

      &-buttons {
        flex: 1 0 auto;
        text-align: right;
      }
    }
  }
}
</style>
